import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { LucideDynamicIcon, LucideTriangleAlert } from '@lucide/angular';
import { TextDirective } from '../../../typography/text.directive';
import { JoiiIcon } from '../../../icons/lucide/icon';

export type BadgeUrgency = 'low' | 'mild' | 'high';
export type BadgeUrgencyType = 'outlined' | 'filled';

@Component({
  selector: 'joii-urgency-badge',
  template: `
    @if (showIcon()) {
      <svg [lucideIcon]="icon()" [strokeWidth]="2" [size]="16" />
    }
    <span joii-text [size]="3" [color]="textColor()" weight="bold"> <ng-content /></span>
  `,
  imports: [TextDirective, LucideDynamicIcon],
  host: {
    '[attr.data-urgency]': 'urgency()',
    '[attr.data-badge-type]': 'type()',
  },
  styles: `
    :host {
      border-radius: var(--Vetai-Radius-2);
      padding: var(--Vetai-Spacing-1-5) var(--Vetai-Spacing-2);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: var(--Vetai-Spacing-1-5);
    }

    :host[data-badge-type='outlined'][data-urgency='low'] {
      background-color: var(--Vetai-Status_packages--Success--Transparent_50);
      border: 1px solid var(--Vetai-Status_packages--Success--Default);
    }
    :host[data-badge-type='outlined'][data-urgency='mild'] {
      background-color: var(--Vetai-Status_packages--Warning--Transparent_50);
      border: 1px solid var(--Vetai-Status_packages--Warning--Default);
    }
    :host[data-badge-type='outlined'][data-urgency='high'] {
      background-color: var(--Vetai-Status_packages--Error--Transparent_50);
      border: 1px solid var(--Vetai-Status_packages--Error--Default);
    }

    :host[data-badge-type='filled'][data-urgency='low'] {
      background-color: var(--Vetai-Status_packages--Success--Default);
      border: 1px solid var(--Vetai-Status_packages--Success--Default);
    }
    :host[data-badge-type='filled'][data-urgency='mild'] {
      background-color: var(--Vetai-Status_packages--Warning--Default);
      border: 1px solid var(--Vetai-Status_packages--Warning--Default);
    }
    :host[data-badge-type='filled'][data-urgency='high'] {
      background-color: var(--Vetai-Status_packages--Error--Default);
      border: 1px solid var(--Vetai-Status_packages--Error--Default);
    }
    /* Lucide icons are outlines; filling them hides inner details such as the alert's "!". */
    :host svg.lucide {
      fill: none;
    }
    :host[data-badge-type='filled'] svg.lucide {
      color: var(--Vetai-Positive_package--Default);
    }
    :host[data-badge-type='outlined'] svg.lucide {
      color: var(--Vetai-Texts--Base);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UrgencyBadgeComponent {
  urgency = input<BadgeUrgency>('low');
  type = input<BadgeUrgencyType>('outlined');
  showIcon = input<boolean>(true);
  textColor = computed(() => (this.type() === 'filled' ? 'positive' : 'base'));
  icon = input<JoiiIcon>(LucideTriangleAlert);
}
